import { useCallback } from 'react';

import { useActivityBuilderContext } from 'approot/activity-builder/activity-builder.context';
import {
  ActivityBuilderDrawerHeader,
  ActivityBuilderDrawerContent,
  ActivityBuilderDrawerWrapper,
} from 'approot/activity-builder/activity-builder-drawer';
import { SecondaryButton } from 'approot/shared/styles/buttons/buttons';
import { Box } from 'approot/shared/design-system/box/box.style';
import { SettingsGroup } from 'approot/activity-builder/settings/settings.style';
import { FileDropperContextProvider } from 'lib/form/file-dropper/file-dropper';
import { FileDropperImage } from 'lib/form/file-dropper/file-dropper-image';
import { ACCEPT_TYPE_IMAGE } from 'lib/form/file-dropper/file-dropper.constants';
import { useAddImageToComponent } from '../component.hooks';
import { CONTENT_IMAGE } from 'global/constants';
import { useGetInteractiveLessonParams } from 'approot/activity-builder/activity-builder.hooks';
import { useActivityBuilderRequest } from 'approot/activity-builder/activity-builder-request.apollo';

const IMAGE_FILE_KEY_FIELD = 'imageKey';

type Props = {
  id: string;
  contentId: number;
  sectionId: string;
  goForward: () => void;
};

const ImageCreateOptions = ({
  id,
  contentId,
  sectionId,
  elementDimensionLabel,
  goForward,
}: Props & {
  elementDimensionLabel: string;
}) => {
  const { closeDrawer } = useActivityBuilderContext();
  const { contentType } = useGetInteractiveLessonParams();

  const { createRequest, clearRequest } = useActivityBuilderRequest();
  const { addImageToComponent, loading } = useAddImageToComponent();

  const handleFileChange = useCallback(
    (key: string, fileName: string) => {
      const requestId = createRequest(id);
      addImageToComponent({
        variables: {
          input: {
            contentId,
            contentType,
            sectionId,
            componentId: id,
            imageInput: {
              key,
              imageType: CONTENT_IMAGE,
              fileName,
            },
          },
        },
      }).finally(() => {
        clearRequest(requestId);
      });
    },
    [
      id,
      contentId,
      sectionId,
      contentType,
      addImageToComponent,
      createRequest,
      clearRequest,
    ]
  );

  return (
    <ActivityBuilderDrawerWrapper>
      <ActivityBuilderDrawerHeader title="Image" onClose={closeDrawer} />
      <ActivityBuilderDrawerContent>
        <Box $marginBottom={16}>
          <FileDropperContextProvider>
            <FileDropperImage
              name={IMAGE_FILE_KEY_FIELD}
              accept={[ACCEPT_TYPE_IMAGE]}
              isFinishing={loading}
              additionalLabel={elementDimensionLabel}
              onChange={handleFileChange}
            />
          </FileDropperContextProvider>
        </Box>

        <SettingsGroup>
          <SecondaryButton
            type="button"
            onClick={goForward}
            data-selector="btn-choose-from-library"
          >
            Choose from Stimulus Library
          </SecondaryButton>
        </SettingsGroup>
      </ActivityBuilderDrawerContent>
    </ActivityBuilderDrawerWrapper>
  );
};

export default ImageCreateOptions;
